import { useContext, useState, useMemo, useEffect } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Card from '@mui/material/Card'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsButton, CvsAutoComplete } from '@components'
import { templateContext } from '@contexts'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { t } from '@lingui/core/macro'
import { Trans, Select } from '@lingui/react/macro'

const dangerButton = css`
  background-color: #ff0000;
  :hover {
    background-color: #da0000;
  }
  color: white;
`

const StyledCard = styled(Card)`
  display: inline-flex;
`
const StyledTypography = styled(Typography)`
  margin: 12px;
`

const Warning = styled.div`
  background-color: #edcf69;
  width: 16px;
`

export const PoweredDatasetModal = ({
  open,
  setOpen,
  singleColumn = false,
  save,
  initialTemplate,
}) => {
  const { byId: templatesById, fetchTemplates } = useContext(templateContext)
  const [DATASETS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const templateOptions = useMemo(
    () =>
      Object.values(templatesById)
        .filter(template => !!template.rollup_dataset_id)
        .map(template => ({
          label: template.name,
          value: template.id,
        })) || [],
    [templatesById],
  )

  const [selectedTemplate, setSelectedTemplate] = useState()
  const [selectedTemplateColumn, setSelectedTemplateColumn] = useState()
  const [templateColumns, setTemplateColumns] = useState()

  useEffect(() => {
    const call = async () => {
      const { data } = await api.get(`/api/v1/account/template/${initialTemplate}`)
      setTemplateColumns(
        data?.template_fields.map(tf => ({
          label: tf.field?.name || t`(Unnamed)`,
          value: tf.id,
        })) || [],
      )
    }
    if (singleColumn && initialTemplate) {
      call()
    }
  }, [initialTemplate])

  useEffect(() => {
    const call = async () => {
      const { data } = await api.get(`/api/v1/account/template/${selectedTemplate}`)
      setTemplateColumns(
        data?.template_fields.map(tf => ({
          label: tf.field?.name || t`(Unnamed)`,
          value: tf.id,
        })) || [],
      )
    }
    if (singleColumn && selectedTemplate) {
      call()
    }
  }, [selectedTemplate, singleColumn])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  const singleColumnSlug = singleColumn ? 'single' : 'multiple'
  const templateOrDataset = DATASETS_ENABLED ? t`Dataset` : t`Template`
  const templateOrDatasetAndComment = singleColumn ? t`Dataset and Column` : t`Template and Column`

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false)
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle>{t`Power column with dataset`}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <StyledCard>
            <Warning />
            <StyledTypography>
              <Trans>
                After selecting a{' '}
                <Select
                  value={singleColumnSlug}
                  _single={templateOrDatasetAndComment}
                  other={templateOrDataset}
                />
                all values that exist for that {templateOrDataset} will be populated into this
                list's values. Those values will not be editable directly from this page, and any
                changes to those values will need to be made by making, editing, or deleting
                submissions. Only data that has been successfully processed will be available as
                options in a pick-list powered by a template. Depending on a number of factors those
                values may take some time to appear in the list. Only a sample of those values will
                be available here, to see all values please navigate to template dataset.
              </Trans>
            </StyledTypography>
          </StyledCard>
        </DialogContentText>
        <CvsAutoComplete
          label={t`Select template`}
          required={true}
          style={{ margin: 0 }}
          options={templateOptions}
          onSelected={item => {
            setSelectedTemplate(item)
          }}
          selected={selectedTemplate || initialTemplate}
        />
        {singleColumn ? (
          <CvsAutoComplete
            label={t`Select column`}
            required={true}
            style={{ margin: 0 }}
            options={templateColumns}
            onSelected={item => {
              setSelectedTemplateColumn(item)
            }}
            selected={selectedTemplateColumn}
            disabled={!(selectedTemplate || initialTemplate)}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        {initialTemplate && (
          <Tooltip title="unpowering this list will remove all current values.">
            <CvsButton
              css={dangerButton}
              onClick={() => {
                setSelectedTemplate(null)
                setSelectedTemplateColumn(null)
                save({ template: null, column: null })
                setOpen(false)
              }}
            >
              {t`Un-power list`}
            </CvsButton>
          </Tooltip>
        )}
        <CvsButton
          onClick={() => {
            setSelectedTemplate(null)
            setSelectedTemplateColumn(null)
            setOpen(false)
          }}
          color="primary"
        >
          {t`Cancel`}
        </CvsButton>
        <CvsButton
          disabled={
            singleColumn
              ? !(selectedTemplate || initialTemplate) || !selectedTemplateColumn
              : !(selectedTemplate || initialTemplate)
          }
          onClick={() => {
            save({
              template: selectedTemplate,
              ...(singleColumn && { column: selectedTemplateColumn }),
            })
            setOpen(false)
          }}
          color="secondary"
        >
          {t`Confirm`}
        </CvsButton>
      </DialogActions>
    </Dialog>
  )
}
